import {
    IndicatorResponse,
    Node,
    NodeType,
    InputType,
    Sequence,
    SequenceType,
    SurveySession,
    Indicator,
    Paragraph,
    OptionGroup,
    SomatConfig,
    SurveySessionNodeProgress,
    SurveySessionProgressSummary, Branch, SequenceElementType, SessionMode, UploadedFile
} from "@/types/models";
import {
    createContext,
    Dispatch,
    PropsWithChildren,
    ReactNode,
    useContext,
    useEffect,
    useReducer,
    useRef,
    useState
} from "react";
import {
    createArrayNode,
    deleteArrayNode,
    getChildren, loadNodePath,
    getNodeTypes,
    getProgressDetail,
    getProgressSummary, flattenHierarchy
} from "@/services/NodeService";
import {
    getInputTypes,
    getOptionGroups,
    getSurveyNodeContent,
    getSurveySequenceTypes, postResponse,
    ResponseUpdate
} from "@/services/SurveyService";
import InputLabel from "@/Components/InputLabel";
import TextInput from "@/Components/TextInput";
import InputError from "@/Components/InputError";
import SecondaryButton from "@/Components/SecondaryButton";
import DangerButton from "@/Components/DangerButton";
import Modal from "@/Components/Modal";
import {toast} from "react-toastify";
import classNames from "classnames";
import {Uppy} from "@uppy/core";
import {router, usePage} from "@inertiajs/react";
import {PageProps} from "@/types";
import {getPermissions} from "@/services/UserService";
import {SurveySessionInertiaProps} from "@/app";
import {indexChar, toRoman} from "@/utils/strings";
import {getBranch} from "@/services/BranchService";
import {forEach} from "lodash";
import {SequencesAction, sequencesReducer} from "@/Reducers/SequencesReducer";
import {NodeAction, nodeReducer} from "@/Reducers/NodeReducer";

export type EvaluatingIndicator = {
    indicator: Indicator
    nodeIndex: number
}

type SurveySessionContextProps = {
    init: (props: SurveySessionInertiaProps) => void
    session: SurveySession | undefined
    setSession: Dispatch<SurveySession | undefined>
    survey: SurveySession['survey'] | undefined
    rootNode: Node | undefined
    nodeTypes: NodeType[] | undefined
    responses: IndicatorResponse[] | undefined
    fetchChildren: (node: Node, setChildren?: (children: Node[]) => void) => Promise<Node[] | undefined>
    fetchNodeContent: (node: Node, nodeIndex?: number) => Promise<Node | undefined>
    sequenceTypes: SequenceType[] | undefined
    inputTypes: InputType[] | undefined
    optionGroups: OptionGroup[] | undefined
    initialNode: Node | undefined
    activeNode: Node | undefined
    setActiveNode: Dispatch<Node | undefined>
    evaluatingIndicator: EvaluatingIndicator | undefined
    setEvaluatingIndicator: Dispatch<EvaluatingIndicator | undefined>
    nodes: Node[]
    setNodes: Dispatch<NodeAction>
    activeArrayNode: Node | undefined
    activateNode: (node: Node, keepNav?: boolean) => void
    nextNode: Node | undefined
    previousNode: Node | undefined
    addArrayNode: (node: Node, setChildren?: (children: Node[]) => void) => Promise<Node>
    removeArrayNode: (node: Node, setChildren?: (children: Node[]) => void) => void
    loading: boolean
    activeNodeIsVisible: boolean
    setActiveNodeIsVisible: (set: boolean) => void
    forceShowActiveNode: boolean
    setForceShowActiveNode: (set: boolean) => void
    respond: (indicator: Indicator, nodeIndex: number | null | undefined, input: string, asText?: boolean) => Promise<IndicatorResponse>
    showingNav: boolean
    setShowingNav: (showing: boolean) => void
    pinnedNav: boolean
    setPinnedNav: (pinned: boolean) => void
    config: SomatConfig
    getAscendants: (node: Node) => Node[]
    action: string | undefined
    setAction: Dispatch<string | undefined>
    progressSummary: SurveySessionProgressSummary | undefined
    fetchProgressSummary: () => Promise<SurveySessionProgressSummary | undefined>
    progressDetail: SurveySessionNodeProgress | undefined
    fetchProgressDetail: (node: Node | undefined) => Promise<SurveySessionNodeProgress | undefined>
    getRouteParams: (node: Node, nodeIndex?: number | undefined) => {session?: number, node?: number, index?: number}
    isReady: () => boolean
    permissions: string[]
    fetchPermissions: (pattern?: string) => Promise<string[]>
    userCan: (action: string) => boolean,
    branches: Record<number, Branch>
    fetchBranch: (id: number) => Promise<Record<number, Branch> | undefined>
    findBranch: (idOrHandle: number | string) => Branch | undefined
    findNode: (idOrHandle: number | string, arrayIndex?: number, includeInline?: boolean) => Node | undefined
    findSequenceElementType: (id: number) => SequenceElementType | undefined
    findIndicator: (idOrHandle: number | string, node?: Node) => Indicator | undefined
    lastResponse: IndicatorResponse | undefined
    lastUpdate: number
    getArrayNode: (node: Node) => Node | undefined
    mode: SessionMode
    setMode: Dispatch<SessionMode>
    panelSizes: Record<string, number>
    setPanelSizes: Dispatch<Record<string, number>>
}

type RemovingArrayNode = {
    node: Node
    setChildren?: (children: Node[]) => void
    confirmed?: boolean
    resolve: (value: Node | undefined) => void
    reject: (error: Error) => void
}

type ManagingFiles = {
    response: IndicatorResponse | null
}

export const debugMode = import.meta.env.VITE_APP_DEBUG === 'true'

const SurveySessionContext = createContext<SurveySessionContextProps | undefined>(undefined)

export const SurveySessionContextProvider = (
    {children, auth, variant, config}: PropsWithChildren<SurveySessionInertiaProps & PageProps>
) => {

    const [survey, setSurvey] = useState<SurveySession['survey'] | undefined>(undefined)
    const [rootNode, setRootNode] = useState<Node | undefined>(undefined)
    const [session, setSession] = useState<SurveySession | undefined>(undefined)
    const [initialNode, setInitialNode] = useState<Node | undefined>(undefined)
    const [nodeTypes, setNodeTypes] = useState<NodeType[] | undefined>(undefined)
    const [inputTypes, setInputTypes] = useState<InputType[] | undefined>(undefined)
    const [optionGroups, setOptionGroups] = useState<OptionGroup[] | undefined>(undefined)
    const [responses, setResponses] = useState<IndicatorResponse[] | undefined>(undefined)
    const [branches, setBranches] = useState<Record<number, Branch>>({})
    const [sequenceTypes, setSequenceTypes] = useState<SequenceType[] | undefined>(undefined)

    const [activeNode, setActiveNode] = useState<Node | undefined>(undefined)
    const [activeArrayNode, setActiveArrayNode] = useState<Node | undefined>(undefined)

    const [nextNode, setNextNode] = useState<Node | undefined>(undefined)
    const [previousNode, setPreviousNode] = useState<Node | undefined>(undefined)
    const [nodes, setNodes] = useReducer(nodeReducer, [])
    const [loading, setLoading] = useState(false)
    const [forceShowActiveNode, setForceShowActiveNode] = useState(true)
    const [activeNodeIsVisible, setActiveNodeIsVisible] = useState(true)
    const [evaluatingIndicator, setEvaluatingIndicator] = useState<EvaluatingIndicator | undefined>(undefined)
    const [removingArrayNode, setRemovingArrayNode] = useState<RemovingArrayNode | undefined>()
    const [showingNav, setShowingNav] = useState(false)
    const [pinnedNav, setPinnedNav] = useState(false)
    const [action, setAction] = useState<string | undefined>(undefined)
    const [progressSummary, setProgressSummary] = useState<SurveySessionProgressSummary | undefined>(undefined)
    const [progressDetail, setProgressDetail] = useState<SurveySessionNodeProgress | undefined>(undefined)
    const [permissions, setPermissions] = useState<string[]>([])
    const [lastResponse, setLastResponse] = useState<IndicatorResponse | undefined>(undefined)
    const [lastUpdate, setLastUpdate] = useState(0)
    const [mode, setMode] = useState<SessionMode>('respond')
    const [panelSizes, setPanelSizes] = useState<Record<string, number>>({})

    const processingInitialNode = useRef(false)
    const isReady = () => !processingInitialNode.current

    const init = (props: SurveySessionInertiaProps) => {
        const isSwitchingSession =
            !!props.session?.id && !!session?.id && props.session.id !== session.id

        if (isSwitchingSession) {
            setActiveNode(undefined)
            setProgressSummary(undefined)
            setProgressDetail(undefined)
            setLastResponse(undefined)
            setResponses(undefined)
            setBranches({})
            setEvaluatingIndicator(undefined)
            setNodes({ type: "SET", nodes: [] })
        }

        const rootNode = props.session?.survey?.rootNode
        setRootNode(rootNode)
        if(rootNode) setNodes({ type: "SET", nodes: flattenHierarchy([rootNode])})
        setSurvey(props.session?.survey)

        console.log("SESSION", props.session)
        console.log("INITIAL NODE", props.initialNode)

        setSession(props.session)
        setInitialNode(props.initialNode)
        setMode(props.mode ?? 'respond')

        setNodeTypes(props.nodeTypes)
        setInputTypes(props.inputTypes)
        setOptionGroups(props.optionGroups)
        setSequenceTypes(props.sequenceTypes)

        setPinnedNav(!!props.session?.survey?.metadata?.navigator)
    }

    /**
     *
     * @param pattern
     */
    const fetchPermissions = async(pattern?: string) => {
        const promise: Promise<string[]> = getPermissions(pattern)
        promise.then((fetchedPermissions: string[]) => {
            setPermissions(Array.from(new Set([...permissions, ...fetchedPermissions])))
        })

        return promise
    }

    const fetchBranch = async (id: number) => {
        const promise: Promise<Record<number, Branch> | undefined> = getBranch(id)
        promise.then((fetchedBranch: Record<number, Branch> | undefined) => {
            if(fetchedBranch) {
                setBranches((current) => ({...current, ...fetchedBranch}))
            }
        })

        return promise
    }

    const findBranch = (idOrHandle: number | string, parent?: Branch): Branch | undefined => {
        const search = parent ? parent.children : branches
        if(!search) return undefined

        let branch = typeof idOrHandle === 'number'
            ? search[idOrHandle]
            : Object.values(search).find(b => b.handle === idOrHandle)

        if(!branch) {
            for(const id in search) {
                branch = findBranch(idOrHandle, search[id])
                if(branch) break;
            }
        }

        return branch
    }

    const findNode = (idOrHandle: number | string, arrayIndex?: number, includeInline: boolean = true): Node | undefined => {
        if (!nodes || nodes.length === 0) return undefined;

        // Helper function to search within a single node
        const searchInNode = (node: Node): Node | undefined => {
            // Check if this node matches our criteria
            const matches = (candidate: Node): boolean => {
                // Include inline nodes if includeInline is true (default), exclude them if false
                if (!includeInline && candidate.inline) return false;

                // Match the ID or handle
                const idMatches = typeof idOrHandle === 'number'
                    ? candidate.id === idOrHandle
                    : candidate.handle === idOrHandle;

                if (!idMatches) return false;

                // If arrayIndex is specified, match it exactly
                if (arrayIndex !== undefined) {
                    return candidate.array_index === arrayIndex;
                }

                return true;
            };

            // Check if the current node matches
            if (matches(node)) {
                return node;
            }

            // If we're including inline nodes, search through this node's content
            if (includeInline && node.content) {
                for (const contentItem of node.content) {
                    // Content can include indicators, paragraphs, and inline nodes
                    if (contentItem._class === 'Node' && matches(contentItem as Node)) {
                        return contentItem as Node;
                    }
                }
            }

            // Search through direct children in the hierarchy
            if (node.children) {
                for (const child of node.children) {
                    const found = searchInNode(child);
                    if (found) return found;
                }
            }

            return undefined;
        };

        // Start search from all top-level nodes
        for (const node of nodes) {
            const found = searchInNode(node);
            if (found) return found;
        }

        // Fallback: if we didn't find with exact array_index matching and no arrayIndex was specified,
        // try to find the base node (without array_index) as fallback
        if (arrayIndex === undefined) {
            for (const node of nodes) {
                const searchForBase = (candidate: Node): Node | undefined => {
                    if (!includeInline && candidate.inline) return undefined;

                    const idMatches = typeof idOrHandle === 'number'
                        ? candidate.id === idOrHandle
                        : candidate.handle === idOrHandle;

                    if (idMatches && (candidate.array_index === undefined || candidate.array_index === null)) {
                        return candidate;
                    }

                    // Search in content for inline nodes
                    if (includeInline && candidate.content) {
                        for (const contentItem of candidate.content) {
                            if (contentItem._class === 'Node') {
                                const found = searchForBase(contentItem as Node);
                                if (found) return found;
                            }
                        }
                    }

                    // Search in children
                    if (candidate.children) {
                        for (const child of candidate.children) {
                            const found = searchForBase(child);
                            if (found) return found;
                        }
                    }

                    return undefined;
                };

                const found = searchForBase(node);
                if (found) return found;
            }
        }

        return undefined;
    };

    /**
     *
     * @param node
     */
    const getArrayNode = (node: Node): Node | undefined => {
        if(node.array_index) return findNode(node.id, node.array_index)

        while(node.parent) {
            if(node.parent.array_index) {
                node.parent = findNode(node.parent_id!, node.parent.array_index)
                return node.parent
            }
            node = node.parent
        }

        return undefined
    }

    /**
     *
     * @param idOrHandle
     * @param node
     */
    const findIndicator = (idOrHandle: number | string, node?: Node): Indicator | undefined => {
        if(!node) node = rootNode!
        let indicator = typeof idOrHandle === 'number'
            ? node?.content?.find(i => i._class === 'Indicator' && i.id === idOrHandle || i.handle === `${idOrHandle}`)
            : node?.content?.find(i => i._class === 'Indicator' && i.handle === idOrHandle)

        if(!indicator) {
            const inlineNodes = node.content?.filter(c => c._class === 'Node') ?? []
            for(const inlineNode of inlineNodes) {
                indicator = findIndicator(idOrHandle, inlineNode)
                if(indicator) break;
            }
        }

        if(!indicator && node.children) {
            for(const child of node.children) {
                indicator = findIndicator(idOrHandle, child)
                if(indicator) break;
            }
        }

        return indicator as Indicator | undefined
    }

    /**
     *
     * @param indicator
     * @param node
     * @param nodeIndex
     * @param acc
     */
    const findAssumers = (indicator: Indicator, node?: Node, nodeIndex: number = 0, acc: Array<Indicator> = []): Indicator[] => {
        if(!node) node = rootNode!

        let assumers = node.content.filter(c =>
            c._class === 'Indicator' && c.assume_from_indicator_id === indicator.id
            && !acc.find(a => a.id === c.id)
        )
        for(const assumer of assumers) {
            if(!assumer._temp) assumer._temp = {}
            assumer._temp.array_index = nodeIndex
        }

        if(node.children) {
            for(const child of node.children) {
                if(child.array_index === nodeIndex + 1) {
                    assumers = [...assumers, ...findAssumers(indicator, child, nodeIndex + 1, acc)]
                } else if(!child.array_max) {
                    assumers = [...assumers, ...findAssumers(indicator, child, nodeIndex, acc)]
                }
            }
        }

        return assumers as Indicator[]
    }

    /**
     *
     * @param id
     */
    const findSequenceElementType = (id: number): SequenceElementType | undefined => {
        if(!sequenceTypes) return undefined

        for(const sequenceType of sequenceTypes) {
            const elementType = sequenceType.element_types?.find(t => t.id === id)
            if(elementType) return elementType
        }

        return undefined
    }

    /**
     * Determines if a user has permission to perform a specific action.
     *
     * The function checks whether the given action matches any entry in the permissions list.
     * Permissions may include wildcards (*) at any level of the action hierarchy.
     *
     * @param {string} action - The action to check against the user's permissions. The action
     *                           is represented as a dot-separated string (e.g., "module.submodule.action").
     * @returns {boolean} - Returns `true` if the user has the necessary permission, otherwise `false`.
     */
    const userCan = (action: string) => {
        const actionParts = action.split(".")
        return permissions.some(p => {
            if(p === action) return true

            const parts = p.split(".")

            for(let i = 0; i < actionParts.length; i++) {
                if(parts[i] !== '*' && parts[i] !== actionParts[i]) return false
            }

            return true
        })
    }

    const fetchChildren = async (node: Node, setChildren?: (children: Node[]) => void) => {
        // if(node.children !== undefined) {
        //     if(setChildren) setChildren(node.children)
        //     return
        // }
        node.children = []
        setLoading(true)
        const promise: Promise<Node[]> = getChildren(node, session);
        promise.then((children: Node[]) => {
            handleChildrenUpdate(node, children, setChildren)
            setLoading(false)
        })

        return promise
    }

    const fetchProgressSummary = async () => {
        const promise = getProgressSummary(session!)
        promise.then((progress: SurveySessionProgressSummary) => {
            setProgressSummary(progress)
        })

        return promise
    }

    const fetchProgressDetail = async (node?: Node) => {
        const promise = getProgressDetail(session!, node)
        promise.then((progress: SurveySessionNodeProgress) => {
            setProgressDetail(progress)
        })

        return promise
    }

    const fetchNodeContent = async (node: Node, nodeIndex?: number | undefined) => {
        if(node.content !== undefined) return
        setLoading(true)
        const promise: Promise<Node> = getSurveyNodeContent(session!, node, nodeIndex)
        promise.then((nodeWithContent: Node) => {
            node.content = nodeWithContent.content
            node.type = node.type || nodeTypes?.find(t => t. id === node.node_type_id)
            setNodes({type: "SET", nodes: [...nodes]})
            setLoading(false)
        })

        return promise
    }

    const getRouteParams = (node: Node, arrayIndex?: number | undefined) => {
        const params: {session?: number, node?: number, index?: number} = {session: session?.id, node: node?.id}
        if(arrayIndex) params.index = arrayIndex

        return params
    }

    const activateNode = (node: Node, keepNav?: boolean) => {
        const found = nodes.find(
            n =>
                n.id === node.id &&
                n.array_index === node.array_index &&
                n.parent_array_index === node.parent_array_index
        )

        setActiveNode(found ?? node)

        if(!keepNav && !pinnedNav) setShowingNav(false)
    }

    const addArrayNode = async (node: Node, setChildren?: (children: Node[]) => void): Promise<Node> => {
        setLoading(true)
        if(!node.parent) node.parent = findNode(node.parent_id!)
        const lastIndex = node.parent!.children!.findLastIndex(c => c.id === node.id)

        const promise: Promise<Node> = createArrayNode(node, session!)
        promise.then((added: Node) => {
            added.parent = node!.parent

            const siblings = node.parent!.children!.filter(c => c.id === node.id && ((c.array_index ?? 0) > 0))
            const arrayCount = siblings.length + 1

            if(arrayCount === 1 && node.parent!.children!.length === 1) {
                node.parent!.children!.splice(0, 1)
            }

            // IMPORTANT: make the same instance consistent, so later lookups use the right array_index.
            added.array_index = arrayCount
            added.array_count = arrayCount

            node!.parent!.children!.splice(lastIndex + 1, 0, added)

            let i = 0
            for (const sibling of siblings) {
                sibling.array_count = arrayCount
                sibling.array_index = ++i
            }

            updateChildren(added)
            updateNodesText(nodes)

            const nextRoot = {...rootNode!}
            setRootNode(nextRoot)
            setNodes({ type: "SET", nodes: flattenHierarchy([nextRoot]) })

            if(arrayCount > 1) {
                const indicators = flattenHierarchy([added]).map(n => n.content).flat().filter(
                    c => c._class === 'Indicator' && !!c.assume_from_indicator_id
                ) as Indicator[]
                for (const indicator of indicators) {
                    const source = findIndicator(indicator.assume_from_indicator_id!, siblings[arrayCount - 2])
                    if(source && source.response) {
                        const asText = !!source.response.input_text
                        const input = asText ? `${source.response.input_text}` : `${source.response.input}`
                        respond(indicator, arrayCount, input, asText, source.response.id)
                    }
                }
            }

            setLoading(false)

            setTimeout(() => {
                const toActivate = findNode(added.id, added.array_index) ?? added
                setActiveNode(toActivate)
            }, 0)
        })

        return promise
    }

    const removeArrayNode = (node: Node, setChildren?: (children: Node[]) => void, confirmed?: boolean) => {
        if(!confirmed) {
            return new Promise<Node | undefined>((resolve, reject) => {
                setRemovingArrayNode({
                    node: node,
                    setChildren: setChildren,
                    confirmed: !!node.metadata?.allow_delete_without_prompt,
                    resolve: resolve,
                    reject: reject
                })
            })
        }
        setLoading(true)

        const promise: Promise<Node> = deleteArrayNode(node, session!)
        promise.then((deleted: Node) => {
            if(node === activeNode) setActiveNode(previousNode ?? nextNode ?? rootNode)
            const index = node.parent!.children!.findIndex(c => c.id === node.id && c.array_index === node.array_index)

            node.parent!.children!.splice(index, 1)
            const allNodes = nodes.filter(n => n.id !== node.id || n.array_index !== node.array_index)
            setNodes({type: "SET", nodes: allNodes})

            const siblings = node.parent!.children!.filter(c => c.id === node.id)
            if(!siblings.length) {
                node.array_count = 0
                node.array_index = 0
                fixNodeText(node)
                node.parent!.children!.splice(index, 0, node)
            } else {
                const arrayCount = siblings.length
                let i = 0
                for (const sibling of siblings) {
                    sibling.array_count = arrayCount
                    sibling.array_index = ++i
                    fixNodeText(sibling)
                }
            }

            if(activeArrayNode?.id === node.id) activateNode(siblings.length ? siblings[siblings.length - 1] : node.parent!)

            removingArrayNode!.resolve(node)
            const flatNodes = flattenHierarchy(nodes)
            updateNodesText(flatNodes)
            setRootNode({...rootNode!})
            toast.info(`${node.title} has been removed.`)
        }).catch((error: Error) => {
            removingArrayNode!.reject(error)
            toast.error(`${node.title} cannot be removed.`)
            console.error(error)
        }).finally(() => {
            setRemovingArrayNode(undefined)
            setLoading(false)
        })

        return promise
    }

    const handleChildrenUpdate = (node: Node, children: Node[], setChildren?: (children: Node[]) => void, newChildren?: Node[]) => {
        if(!newChildren) newChildren = linkChildren(node, children)
        const allNodes = [...nodes, ...newChildren]
        setNodes({type: "SET", nodes: allNodes})
        checkCanMove(allNodes)
        if(setChildren) setChildren(node.children!)
    }

    const updateChildren = (node: Node, index: number = 0) => {
        if(!node.inline) {
            const x = nodes.findIndex(n => n.id === node.id)
            if (x === -1)
                setNodes({type: "SET", nodes: [...nodes, node]})
            else
                setNodes({type: "SET", nodes: [...nodes.slice(0, x), node, ...nodes.slice(x + 1)]})
        }
        node.type = node.type || nodeTypes?.find(t => t. id === node.node_type_id)
        node.index = index
        if (index) {
            if (node.inline) {
                const indexString = node.parent && node.parent.inline ? toRoman(index).toLowerCase() : indexChar(index)
                node.prefix = node.parent && node.parent.prefix ? `${node.parent.prefix}${indexString}.` : `${indexString}.`
            } else {
                node.prefix = node.parent && node.parent.prefix ? `${node.parent.prefix}${index}.` : `${index}.`
            }
        }

        const inlineNodes = node.content?.filter(c => c._class === 'Node') ?? []
        let i = 0
        inlineNodes.forEach((c, i) => {
            c.parent = node
            updateChildren(c, ++i)
        })

        if (!node.children) return

        node.children.forEach(c => {
            c.parent = node
            c.child_index = i
            updateChildren(c, ++i)
        })
    }

    const linkChildren = (node: Node, children?: Node[], skipAddToArray?: boolean) => {
        const newChildren = []

        if(node.children === undefined) node.children = []
        if(children === undefined) children = node.children
        let childIndex: number = 0
        for (const child of children) {
            child.parent = node
            child.child_index = childIndex++

            child.xlft = child.lft
            child.xrgt = child.rgt
            child.xtitle = child.title
            child.xname = child.name
            child.xdescription = child.description

            if(!node.children.find(c => c.id === child.id)) {
                node.children.push(child)
                newChildren.push(child)
            }
            if(child.array_max && !skipAddToArray) {
                if(child.array_count) {
                    child.array_index = 1
                    for(let i = 2; i <= child.array_count; i++) {
                        const sibling = includeArrayNode(child)
                        if(sibling) childIndex++
                    }
                } else {
                    child.array_index = 0
                }
            }

            fixNodeText(child)
        }
        node.children_aware = true
        if(node === survey!.rootNode) setRootNode({...node})

        updateTreeEdges()

        return newChildren
    }

    const findArrayIndex = (node: Node): number | undefined => {
        let current: Node | undefined = node
        while(current && current.array_index === undefined) {
            current = current.parent
        }

        return current?.array_index
    }

    const includeArrayNode = (node: Node): Node | null => {
        if(!node.array_max) {
            console.warn("Can't add array node without array_max.", node)
            return null
        }

        const parent = node.parent
        if(!parent) {
            console.warn("Can't add array node without parent.", node)
            return null
        }

        const siblings = parent.children?.filter(n => n.id === node.id) ?? []
        if(siblings.length >= node.array_max) {
            console.warn("Can't add array node because array_max reached.", node)
            return null
        }

        let index: number = node.array_index ?? 0
        if(index === 0) {
            node.array_index = 1
            fixNodeText(node)

            return node
        }

        const sibling = {
            ...node, array_index: siblings.length + 1, child_index: parent!.children!.length + 1
        }
        fixNodeText(sibling)

        parent.children!.push(sibling)

        return sibling
    }

    const updateTreeEdges = () => {
        const updateNodeEdges = (node: Node, i: number): number => {
            node.lft = ++i
            if(node.children) for(const child of node.children) i = updateNodeEdges(child, i)
            node.rgt = ++i

            return i
        }

        updateNodeEdges(survey!.rootNode!, 0)
    }

    const fixNodeText = (node: Node | null): void => {
        if(!node) return

        let index = node.array_index
        if(index === undefined) index = findArrayIndex(node)
        if(!index) return

        const idx = `${index}`
        node.title = (node.xtitle ?? node.title).replace("~array_index~", idx)
        node.name = (node.xname ?? node.name).replace("~array_index~", idx)
        if(node.description) node.description = (node.xdescription ?? node.description).replace("~array_index~", idx)
    }

    const getLeaf = (node: Node) => {
        if(node.children !== undefined && node.children.length > 0) {
            return getLeaf(node.children[node.children.length - 1])
        }

        return node
    }

    const checkCanMove = (nodes: Node[], activeArrayNode?: Node) => {
        if(!activeNode) {
            setNextNode(survey?.rootNode)
            setPreviousNode(undefined)
            return
        }

        const flatNodes = flattenHierarchy(nodes)
        const activeNodeIndex = flatNodes.findIndex(
            activeArrayNode
                ? n => n.id === activeNode.id && getArrayNode(n) === activeArrayNode
                : n => n.id === activeNode.id
        )

        const previousNode = activeNodeIndex > 0 ? flatNodes[activeNodeIndex - 1] : undefined
        const nextNode = activeNodeIndex < flatNodes.length - 1 ? flatNodes[activeNodeIndex + 1] : undefined

        updateNodesText(flatNodes)

        setPreviousNode(previousNode)
        setNextNode(nextNode)

        console.log("previousNode", previousNode)
        console.log("nextNode", nextNode)
    }

    const respond = async(indicator: Indicator, nodeIndex: number | null | undefined, input: string, asText?: boolean, assumedFromResponseId: number | undefined = undefined) => {
        const promise = postResponse(session!, {
            indicator_id: indicator.id,
            node_index: nodeIndex,
            input: asText ? undefined : input,
            input_text: asText ? input : undefined,
            assumed_from_response_id: assumedFromResponseId,
            evaluation: mode === 'evaluate' ? true : undefined
        });

        promise.then((response: IndicatorResponse) => {
            indicator.response = response

            setLastResponse(response)
            setLastUpdate(Date.now())
            fetchProgressSummary()

            const assumers = findAssumers(indicator, rootNode!, nodeIndex ?? 0)
            for(const assumer of assumers) {
                respond(assumer, assumer._temp.array_index, input, asText, response.id)
            }

            updateNodesText(nodes)
        }).catch((error: Error) => {
            console.warn(`Response failed: ${error.message}`)
            throw(error)

        })

        return promise;
    }

    const respondUpload = async(indicator: Indicator, nodeIndex: number | null | undefined, files: File[]) => {

    }

    const respondFileDescription = async(response: Response, description: string) => {

    }

    const getAscendants = (node: Node): Node[] => {
        const ascendants: Node[] = []
        while(node.parent) {
            node = node.parent
            ascendants.push(node)
        }

        return ascendants
    }

    const getBackgroundImg = (node: Node): string | undefined => {
        if(node.metadata?.img) return `${node.metadata.img}`
        if(node.parent) return getBackgroundImg(node.parent)
        return undefined
    }

    const updateNodesText = (flatNodes: Node[]) => {
        const fixText = (template: string, node: Node) => {
            let x = 0
            let i = template.indexOf("~")
            let fixed: string = ""
            while(i !== -1) {
                const end = template.indexOf("~", i + 1)
                if(end === -1) {
                    fixed += template.substring(x)
                    break;
                }

                fixed += template.substring(x, i)
                const chk = template.indexOf("|", i + 1)
                const key = template.substring(i + 1, chk !== -1 ? chk : end)
                const other = chk > -1 ? template.substring(chk + 1, end) : undefined
                if(key === "array_index") {
                    fixed += node.array_index ?? 0
                } else if(key === "node_index") {
                    fixed += node.child_index ?? 0
                } else if(key === "node_id") {
                    fixed += node.id
                } else {
                    const indicator = findIndicator(key, node)
                    if(indicator && indicator.response && (indicator.response.input || indicator.response.input_text)) {
                        fixed += indicator.response.input ?? indicator.response.input_text
                    } else {
                        fixed += other ?? template.substring(i, end)
                    }
                }

                x = end + 1
                i = template.indexOf("~", x)
            }
            if(x < template.length) fixed += template.substring(x)

            return fixed
        }
        for(const node of flatNodes) {
            if(node.metadata?.suffix) {
                node.suffix = fixText(`${node.metadata.suffix}`, node)
                node.processedTitle = `${node.title} ${node.suffix}`
            }
        }

        setLastUpdate(Date.now())
    }

    useEffect(() => {
        if(removingArrayNode && removingArrayNode.confirmed) {
            removeArrayNode(removingArrayNode.node, removingArrayNode.setChildren, true)
        }
    }, [removingArrayNode])

    useEffect(() => {
        setForceShowActiveNode(false)
    }, [activeNodeIsVisible])

    useEffect(() => {
        const arrayNode = activeNode ? getArrayNode(activeNode) : undefined
        setActiveArrayNode(arrayNode)
        checkCanMove(nodes, arrayNode)
        setForceShowActiveNode(false)

        if(activeNode) {
            const url = route(`survey.session.${mode}`, getRouteParams(activeNode, arrayNode?.array_index ?? undefined))
            if (window.location.pathname !== url) window.history.pushState({}, '', url)
        }
    }, [activeNode, pinnedNav])

    useEffect(() => {
        if(rootNode) updateChildren(rootNode)
        setActiveNode(initialNode ? findNode(initialNode.id, initialNode.array_index) : rootNode)
    }, [rootNode, initialNode])

    useEffect(() => {
        if(session) {
            fetchProgressSummary()
        }
    }, [session])

    useEffect(() => {
        if(survey) setRootNode(survey.rootNode)
    }, [survey])

    useEffect(() => {
        if(!activeNode) return
        const node = nodes.find(n => n.id === activeNode.id && n.array_index === activeNode.array_index && n.parent_array_index === activeNode.parent_array_index)
        if(node) setActiveNode(node)
    }, [nodes])

    return <SurveySessionContext.Provider value={{
        init,
        session,
        setSession,
        survey,
        rootNode,
        nodeTypes,
        responses,
        fetchChildren,
        fetchNodeContent,
        sequenceTypes,
        inputTypes,
        optionGroups,
        initialNode,
        activeNode,
        setActiveNode,
        evaluatingIndicator,
        setEvaluatingIndicator,
        nodes,
        setNodes,
        activeArrayNode,
        activateNode,
        nextNode,
        previousNode,
        addArrayNode,
        removeArrayNode,
        loading,
        forceShowActiveNode,
        setForceShowActiveNode,
        activeNodeIsVisible,
        setActiveNodeIsVisible,
        respond,
        showingNav,
        setShowingNav,
        pinnedNav,
        setPinnedNav,
        config,
        getAscendants,
        action,
        setAction,
        progressSummary,
        fetchProgressSummary,
        progressDetail,
        fetchProgressDetail,
        getRouteParams,
        isReady,
        permissions,
        fetchPermissions,
        userCan,
        branches,
        fetchBranch,
        findBranch,
        findSequenceElementType,
        findIndicator,
        findNode,
        lastResponse,
        lastUpdate,
        getArrayNode,
        mode,
        setMode,
        panelSizes,
        setPanelSizes,
    }}>
        {children}

        {/* TODO - make this use the universal Modal */}
        <Modal show={removingArrayNode !== undefined && !removingArrayNode.confirmed} onClose={() => setRemovingArrayNode(undefined)}>
            <div className="p-6">
                <h2 className="text-lg font-medium text-heading dark:text-heading-dark">
                    Delete {removingArrayNode?.node.title}?
                </h2>

                <p className="mt-1 text-sm text-text">
                    Are you sure you want to delete all responses and data for
                    {" "}{removingArrayNode?.node.title ?? "this item"}{removingArrayNode?.node.array_index ? ` ${removingArrayNode.node.array_index} of  ${removingArrayNode.node.array_count}` : ''}?
                </p>

                <div className="mt-6 flex justify-end">
                    <SecondaryButton onClick={() => setRemovingArrayNode(undefined)}>Cancel</SecondaryButton>

                    <DangerButton className="ms-3" disabled={loading}
                        onClick={() => setRemovingArrayNode({...removingArrayNode!, confirmed: true})}>
                        Delete {nodeTypes?.find(t => t.id === removingArrayNode?.node.node_type_id!)?.name}
                    </DangerButton>
                </div>
            </div>
        </Modal>
    </SurveySessionContext.Provider>
}

export function useSurveySessionContext(): SurveySessionContextProps {
    const context = useContext(SurveySessionContext);
    if (!context) throw new Error("No SurveySessionContext is defined")
    return context
}

export default SurveySessionContext
